/**
   * Heizlastrechner
   *
   * Formel (Modus "flaeche"):
   *   Watt = Flaeche (m²) x W/m² der Baujahrklasse x Raumtyp-Faktor
   *   Bei Waermepumpe zusaetzlich: / 1,85
   *
   * Formel (Modus "watt"):
   *   Flaeche = Watt / (W/m² x Raumtyp-Faktor)
   *   Bei Waermepumpe wird die Eingabe vorher mit 1,85 hochgerechnet,
   *   weil der Nutzer dort die reale Waermepumpen-Leistung eingibt.
   */
  class HeizlastRechner extends HTMLElement {
    connectedCallback() {
      this.faktorWp = parseFloat(this.dataset.faktorWp) || 1.85;
      this.faktorBad = parseFloat(this.dataset.faktorBad) || 1.15;

      this.out = {
        wert: this.querySelector('[data-out="wert"]'),
        einheit: this.querySelector('[data-out="einheit"]'),
        kontext: this.querySelector('[data-out="kontext"]'),
      };

      this.modus = 'flaeche';
      this.nf = new Intl.NumberFormat('de-DE', { maximumFractionDigits: 0 });
      this.nf1 = new Intl.NumberFormat('de-DE', { maximumFractionDigits: 1 });

      this.addEventListener('input', () => this.rechnen());
      this.addEventListener('change', (e) => {
        this.radioZustand(e.target);
        this.rechnen();
      });

      this.querySelectorAll('.hlr__mode').forEach((btn) => {
        btn.addEventListener('click', () => this.modusWechseln(btn));
      });

      this.rechnen();
    }

    /* Sichtbaren Zustand der Radio-Karten nachziehen */
    radioZustand(target) {
      if (!target || target.type !== 'radio') return;
      this.querySelectorAll(`input[name="${target.name}"]`).forEach((input) => {
        input.closest('.hlr__radio')?.classList.toggle('is-active', input.checked);
      });
    }

    modusWechseln(btn) {
      this.modus = btn.dataset.mode;

      this.querySelectorAll('.hlr__mode').forEach((b) => {
        const aktiv = b === btn;
        b.classList.toggle('is-active', aktiv);
        b.setAttribute('aria-selected', String(aktiv));
      });

      this.querySelector('[data-field="flaeche"]')?.classList.toggle('is-hidden', this.modus !== 'flaeche');
      this.querySelector('[data-field="watt"]')?.classList.toggle('is-hidden', this.modus !== 'watt');

      this.rechnen();
    }

    wert(selektor) {
      return parseFloat(this.querySelector(selektor)?.value) || 0;
    }

    gewaehlt(name) {
      return this.querySelector(`input[data-input="${name}"]:checked`)?.value;
    }

    rechnen() {
      const wm2 = this.wert('[data-input="baujahr"]');
      const istWp = this.gewaehlt('system') === 'wp';
      const istBad = this.gewaehlt('raumtyp') === 'bad';
      const raumFaktor = istBad ? this.faktorBad : 1;

      if (this.modus === 'flaeche') {
        const flaeche = this.wert('[data-input="flaeche"]');
        if (flaeche <= 0) return this.leeren();

        let watt = flaeche * wm2 * raumFaktor;
        if (istWp) watt = watt / this.faktorWp;

        watt = Math.round(watt / 10) * 10;

        this.out.wert.textContent = this.nf.format(watt);
        this.out.einheit.textContent = 'Watt';
        this.out.kontext.textContent = this.kontextText(flaeche, istBad, istWp);
        this.variantenMarkieren(watt);
      } else {
        let watt = this.wert('[data-input="watt"]');
        if (watt <= 0) return this.leeren();

        /* Eingegebene Leistung ist bei WP die reale Leistung -> auf Normleistung zurueckrechnen */
        if (istWp) watt = watt * this.faktorWp;

        const flaeche = watt / (wm2 * raumFaktor);

        this.out.wert.textContent = this.nf1.format(flaeche);
        this.out.einheit.textContent = 'm²';
        this.out.kontext.textContent = this.kontextText(null, istBad, istWp);
        this.variantenMarkieren(null);
      }
    }

    kontextText(flaeche, istBad, istWp) {
      const teile = [];
      if (flaeche) teile.push(`${this.nf1.format(flaeche)} m²`);
      teile.push(istBad ? 'Badezimmer, 24 °C' : 'Wohnraum, 20 °C');
      teile.push(istWp ? 'Wärmepumpe 55/45 °C' : 'Standardheizung 75/65 °C');
      return `Für ${teile.join(' · ')}`;
    }

    /* Kleinste Variante markieren, die den Bedarf deckt */
    variantenMarkieren(bedarf) {
      const items = [...this.querySelectorAll('.hlr__variant')];
      if (!items.length) return;

      items.forEach((li) => {
        li.classList.remove('is-recommended', 'is-too-small');
        li.querySelector('[data-flag]')?.setAttribute('hidden', '');
      });

      if (!bedarf) return;

      const passend = items
        .filter((li) => Number(li.dataset.watt) >= bedarf && li.dataset.available === 'true')
        .sort((a, b) => Number(a.dataset.watt) - Number(b.dataset.watt))[0];

      items.forEach((li) => {
        if (Number(li.dataset.watt) < bedarf) li.classList.add('is-too-small');
      });

      if (passend) {
        passend.classList.add('is-recommended');
        passend.querySelector('[data-flag]')?.removeAttribute('hidden');
      }
    }

    leeren() {
      this.out.wert.textContent = '–';
      this.out.kontext.textContent = '';
      this.variantenMarkieren(null);
    }
  }

  if (!customElements.get('heizlast-rechner')) {
    customElements.define('heizlast-rechner', HeizlastRechner);
  }
